/**
 * AI document wizard → manual wizard handoff.
 *
 * When Kai surfaces a rate-card confirmation (the `rate_card_diff` elicitation),
 * the preview carries a `create_payload`: the exact `api_create_rate_card`
 * arguments the backend would persist. This module turns that payload into the
 * manual wizard's session state so a user can tweak lanes, surcharges, and
 * policies before creating the card themselves.
 *
 * Kept free of React so the mapping is unit-testable in isolation.
 */
import type { OverweightPolicy } from "@/types/api";

import { EQUIPMENT_TYPES } from "../wizardTypes";
import type {
  BookingPoliciesState,
  DdDefaultsState,
  EquipmentConfig,
  PortGroupCreate,
  RateRestrictions,
  RoutePair,
  SpotPaymentBasis,
  Surcharge,
  SurchargesBySize,
} from "../wizardTypes";

type EquipmentType = (typeof EQUIPMENT_TYPES)[number];

/**
 * Coerce a Kai-derived overweight policy into the manual wizard's shape. The
 * backend `create_payload` omits `equipment_types` / `equipment_sizes` (they
 * default to "all"), so the editor — which calls `.join()` on those arrays —
 * would crash without these defaults.
 */
function normalizeOverweightPolicy(
  raw: Partial<OverweightPolicy>,
): OverweightPolicy {
  return {
    ...raw,
    equipment_types: Array.isArray(raw.equipment_types)
      ? raw.equipment_types
      : [],
    equipment_sizes: Array.isArray(raw.equipment_sizes)
      ? raw.equipment_sizes
      : [],
    currency: raw.currency ?? "USD",
    steps: Array.isArray(raw.steps) ? raw.steps : [],
  };
}

/** One surcharge as carried on a Kai `create_payload` line. */
export interface KaiCreatePayloadSurcharge {
  code?: string;
  amount?: number;
  currency?: string;
  basis?: string;
  per?: string;
  category?: string | null;
}

/** One rate line on a Kai `create_payload` (mirrors MCP `RateLineInput`). */
export interface KaiCreatePayloadLine {
  origin_port: string;
  destination_port: string;
  origin?: string | null;
  destination?: string | null;
  equipment_size?: string | null;
  equipment_type?: string | null;
  currency?: string | null;
  frt?: number | null;
  surcharges?: KaiCreatePayloadSurcharge[] | null;
  dd_free_days?: number | null;
  dd_daily_rate?: number | null;
  dd_policy_type?: string | null;
}

/**
 * One source file referenced on a Kai `create_payload` (provenance). Resolves
 * back to the uploaded image/PDF via the Kai conversation + attachment id, so
 * the manual wizard can still show the original after the handoff (where the
 * in-memory `File` objects are gone).
 */
export interface KaiSourceAttachment {
  attachment_id?: string;
  conversation_id?: string;
  filename?: string;
  content_type?: string | null;
}

/** The `create_payload` block on a `rate_card_diff` elicitation preview. */
export interface KaiCreatePayload {
  carrier?: string;
  contract_ref?: string | null;
  named_account?: string | null;
  source?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  is_spot?: boolean;
  lines?: KaiCreatePayloadLine[];
  overweight_policies?: unknown;
  booking_policies?: {
    max_containers?: number | null;
    min_containers?: number | null;
    min_teu?: number | null;
  } | null;
  source_attachments?: KaiSourceAttachment[];
}

/** A resolvable source document reference carried into the manual wizard. */
export interface DraftSourceAttachment {
  conversationId: string;
  attachmentId: string;
  filename: string;
}

/**
 * Keep only source attachments that can actually be fetched (both a
 * conversation id and an attachment id), normalizing the shape the manual
 * wizard / preview expect.
 */
export function normalizeSourceAttachments(
  raw: KaiSourceAttachment[] | undefined,
): DraftSourceAttachment[] {
  if (!Array.isArray(raw)) return [];
  const out: DraftSourceAttachment[] = [];
  for (const a of raw) {
    const conversationId = (a?.conversation_id ?? "").trim();
    const attachmentId = (a?.attachment_id ?? "").trim();
    if (!conversationId || !attachmentId) continue;
    out.push({
      conversationId,
      attachmentId,
      filename: (a?.filename ?? "").trim() || "source document",
    });
  }
  return out;
}

/** The hydrated manual-wizard state produced from one create payload. */
export interface DraftWizardState {
  carrier: string;
  contractRef: string;
  namedAccount: string;
  validityStart: string;
  validityEnd: string;
  paymentBasis: SpotPaymentBasis;
  agent: string;
  routePairs: RoutePair[];
  createdPortGroups: PortGroupCreate[];
  surchargesBySize: SurchargesBySize;
  overweightPolicies: OverweightPolicy[];
  bookingPolicies: BookingPoliciesState;
  ddDefaults: DdDefaultsState;
  restrictions: RateRestrictions;
  /** Source files (image/PDF) this draft was generated from, for cross-check. */
  sourceAttachments: DraftSourceAttachment[];
}

function adHocGroupId(locode: string): string {
  return `adhoc-${locode.toUpperCase()}`;
}

function displayName(name: string | null | undefined, locode: string): string {
  const upper = locode.toUpperCase();
  const trimmed = name?.trim();
  return trimmed ? `${trimmed} (${upper})` : upper;
}

/**
 * Map a Kai/carrier equipment-type code to the manual wizard's canonical set
 * (`EQUIPMENT_TYPES`). Carrier offers use ISO-ish codes (GP = general purpose,
 * DC = dry container, HQ = high cube, RF = reefer, …) that aren't the wizard's
 * labels, so an unmapped value would leave the equipment TYPE select blank.
 * Unknown codes fall back to "DV" so the select is always populated.
 */
function normalizeEquipmentType(raw: string | null | undefined): EquipmentType {
  const t = (raw ?? "").trim().toUpperCase();
  switch (t) {
    case "HC":
    case "HQ":
    case "HICUBE":
    case "HIGHCUBE":
      return "HC";
    case "RF":
    case "RE":
    case "RH":
    case "RT":
    case "REEFER":
      return "Reefer";
    case "OT":
    case "FR":
    case "PL":
    case "OOG":
      return "OOG";
    case "DG":
      return "DG";
    // GP / DC / DV / SD / STD and anything else → dry van (the common default).
    default:
      return (EQUIPMENT_TYPES as readonly string[]).includes(t)
        ? (t as EquipmentType)
        : "DV";
  }
}

function mapSurcharge(s: KaiCreatePayloadSurcharge, lineCurrency: string): Surcharge {
  return {
    code: s.code ?? "",
    amount: typeof s.amount === "number" ? s.amount : 0,
    currency: s.currency ?? lineCurrency,
    basis: s.basis ?? s.per ?? "container",
    category: s.category ?? null,
  };
}

/**
 * Parse the prepaid agent slug from a Kai `source` ("agent:toptrans") string.
 * Returns `["prepaid", slug]` for a prepaid rate, `["collect", ""]` otherwise.
 */
export function parseSourceOwnership(
  source: string | null | undefined,
): [SpotPaymentBasis, string] {
  const trimmed = (source ?? "").trim();
  if (trimmed.toLowerCase().startsWith("agent:")) {
    return ["prepaid", trimmed.slice("agent:".length).trim().toLowerCase()];
  }
  return ["collect", ""];
}

/**
 * Reconstruct the manual wizard's session state from a Kai `create_payload`.
 *
 * Lines are grouped into origin→destination route pairs (one equipment row per
 * distinct size+type), each port becoming an ad-hoc single-port group — the
 * same shape the edit/clone flow produces via `reconstructFromLines`.
 */
export function hydrateWizardStateFromPayload(
  payload: KaiCreatePayload,
): DraftWizardState {
  const [paymentBasis, agent] = parseSourceOwnership(payload.source);
  const lines = payload.lines ?? [];

  const adHocGroups: PortGroupCreate[] = [];
  const adHocSeen = new Set<string>();
  const ensureGroup = (locode: string, name: string | null | undefined): string => {
    const upper = locode.trim().toUpperCase();
    const id = adHocGroupId(upper);
    if (!adHocSeen.has(id)) {
      adHocSeen.add(id);
      const label = displayName(name, upper);
      adHocGroups.push({
        id,
        name: label,
        region: "Single Port",
        ports: [upper],
        port_names: { [upper]: label },
      });
    }
    return id;
  };

  const routeMap = new Map<
    string,
    { originId: string; destId: string; equip: Map<string, EquipmentConfig> }
  >();
  const surchargesBySize: SurchargesBySize = {};

  for (const line of lines) {
    if (!line.origin_port || !line.destination_port) continue;
    const originId = ensureGroup(line.origin_port, line.origin);
    const destId = ensureGroup(line.destination_port, line.destination);
    const routeKey = `${originId}::${destId}`;
    if (!routeMap.has(routeKey)) {
      routeMap.set(routeKey, { originId, destId, equip: new Map() });
    }
    const route = routeMap.get(routeKey)!;
    const size = line.equipment_size || "40";
    const type = normalizeEquipmentType(line.equipment_type);
    const eqKey = `${size}-${type}`;
    if (!route.equip.has(eqKey)) {
      route.equip.set(eqKey, {
        size,
        type,
        base_rate: typeof line.frt === "number" ? line.frt : 0,
      });
    }

    const sizeKey = line.equipment_size || "default";
    if (!surchargesBySize[sizeKey] && line.surcharges && line.surcharges.length > 0) {
      const currency = line.currency || "USD";
      surchargesBySize[sizeKey] = line.surcharges.map((s) =>
        mapSurcharge(s, currency),
      );
    }
  }

  const routePairs: RoutePair[] = Array.from(routeMap.values()).map((r, i) => ({
    id: `ai-rp-${i}`,
    origin_id: r.originId,
    destination_id: r.destId,
    equipment: Array.from(r.equip.values()),
  }));

  const overweightPolicies: OverweightPolicy[] = Array.isArray(
    payload.overweight_policies,
  )
    ? (payload.overweight_policies as Partial<OverweightPolicy>[]).map(
        normalizeOverweightPolicy,
      )
    : [];

  const bookingPolicies: BookingPoliciesState = {
    max_containers: payload.booking_policies?.max_containers ?? undefined,
    min_containers: payload.booking_policies?.min_containers ?? undefined,
    min_teu: payload.booking_policies?.min_teu ?? undefined,
  };

  const firstDd = lines.find(
    (l) => l.dd_free_days != null && l.dd_free_days >= 0,
  );
  const ddDefaults: DdDefaultsState = firstDd
    ? {
        free_days: firstDd.dd_free_days ?? undefined,
        daily_rate: firstDd.dd_daily_rate ?? undefined,
        policy_type: firstDd.dd_policy_type ?? undefined,
      }
    : {};

  return {
    carrier: payload.carrier ?? "",
    contractRef: payload.contract_ref ?? "",
    namedAccount: payload.named_account ?? "",
    validityStart: payload.valid_from ?? "",
    validityEnd: payload.valid_to ?? "",
    paymentBasis,
    agent,
    routePairs,
    createdPortGroups: adHocGroups,
    surchargesBySize,
    overweightPolicies,
    bookingPolicies,
    ddDefaults,
    restrictions: { teu_limits: [], commodity_restrictions: [] },
    sourceAttachments: normalizeSourceAttachments(payload.source_attachments),
  };
}

// ── Session handoff ────────────────────────────────────────────────────────

/**
 * Session key for the AI draft handoff. Deliberately NOT prefixed with
 * `rate-wizard.` so `clearWizardSession()` leaves it intact — the manual wizard
 * consumes and clears it explicitly on mount.
 */
export const AI_DRAFT_SESSION_KEY = "rate-wizard-ai-draft";

/** A queue of create payloads handed off to the manual wizard. */
export interface AiDraftSession {
  payloads: KaiCreatePayload[];
  index: number;
}

export function writeAiDraftSession(session: AiDraftSession): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(AI_DRAFT_SESSION_KEY, JSON.stringify(session));
  } catch {
    /* quota / serialization errors are non-fatal */
  }
}

export function readAiDraftSession(): AiDraftSession | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(AI_DRAFT_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AiDraftSession;
    if (!parsed || !Array.isArray(parsed.payloads)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearAiDraftSession(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(AI_DRAFT_SESSION_KEY);
}
